import { CellValueChangedEvent } from "@ag-grid-community/core";
import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React from "react";
import { MaterialIcon } from "../../../components/shared/MaterialIcon";
import { ColumnKeyEnum } from "../../../constants";
import { IRegistryDataUpdateItem } from "../../../models/RegistryData";
import {
  COL_OPTIONS_YESNO,
  IRegistryDataColumn,
  RegistryDataColumnTypeEnum,
} from "../../../models/RegistryDataColumn";
import { useSetIsEditMode } from "../stores/actions/useSetIsEditMode";
import { useSelectRegistryId } from "../stores/select/useSelectRegistryId";
import { EditsReviewModal } from "./EditsReviewModal";

interface IProps {
  gridRef: any;
  isGridReady: boolean;
}

interface IEditTransaction {
  s3Location?: any;
  recordId: string;
  fieldVersionId?: string;
  fieldVersionMapping?: string;
  field: string;
  isDict?: boolean;
  isPhi?: boolean;
  isRequired?: boolean;
  oldValueDisplay: any;
  oldValueDict?: any;
  oldValue: any;
  newValueDisplay: any;
  newValueDict?: any;
  newValue: any;
}

const RegistryEditMode = ({ gridRef, isGridReady }: IProps) => {
  const setIsEditMode = useSetIsEditMode();
  const [undoSize, setUndoSize] = React.useState(0);
  const [redoSize, setRedoSize] = React.useState(0);
  const [stagedEdits, setStagedEdits] = React.useState<{
    [key: string]: IEditTransaction;
  }>({});
  const [preparedEdits, setPreparedEdits] = React.useState<
    IRegistryDataUpdateItem[]
  >([]);

  const toast = useToast();

  const registryId = useSelectRegistryId();

  const {
    isOpen: isOpenEditsReviewModal,
    onOpen: onOpenEditsReviewModal,
    onClose: onCloseEditsReviewModal,
  } = useDisclosure();

  const handleCloseEditMode = () => {
    const gridApi = gridRef.current.api;

    if (gridApi) {
      if (gridApi.getCurrentUndoSize()) {
        toast({
          title: "Reverting edits..",
          status: "info",
          duration: 2000,
          isClosable: true,
        });

        // revert all edits
        Array.from(Array(gridApi.getCurrentUndoSize()).keys()).forEach((i) =>
          gridApi.undoCellEditing()
        );
      }

      gridApi.stopEditing(true);

      setTimeout(() => {
        setIsEditMode({ isEditMode: false });
      }, 0);
    }
  };

  const handleUndo = React.useCallback(() => {
    gridRef.current.api.undoCellEditing();
  }, [gridRef]);

  const handleRedo = React.useCallback(() => {
    gridRef.current.api.redoCellEditing();
  }, [gridRef]);

  const handleSaveEdits = () => {
    const preparedEditsUpdate = Object.values(stagedEdits).map((edit) => {
      const item: IRegistryDataUpdateItem = {
        record_id: edit.recordId,
        fieldversion_id: edit.fieldVersionId,
        fieldversion_mapping: edit.fieldVersionMapping,
        field: edit.field,
        isPhi: edit.isPhi,
        isDict: edit.isDict,
        isRequired: edit.isRequired,
        oldValue: edit.oldValue,
        oldValueDisplay: edit.oldValueDisplay,
        oldValueDict: edit.oldValueDict,
        newValue: edit.newValue,
        newValueDisplay: edit.newValueDisplay,
        newValueDict: edit.newValueDict,
        s3Location: edit.s3Location,
      };

      return item;
    });

    setPreparedEdits(preparedEditsUpdate);
    onOpenEditsReviewModal();
  };

  const handleEditEvent = React.useCallback(
    (currentChange: IEditTransaction, event: CellValueChangedEvent) => {
      setStagedEdits((prevState) => {
        const stagedEditsUpdate = { ...prevState };

        const changeKey = `${currentChange.recordId}-${currentChange.field}`;

        let isDeleteKey = false;

        if (
          JSON.stringify(currentChange.newValue) !==
          JSON.stringify(currentChange.oldValue)
        ) {
          if (prevState[changeKey]) {
            // This edit has been performed already
            // Check if the new value is the same as the old value

            if (
              JSON.stringify(currentChange.newValue) ===
              JSON.stringify(prevState[changeKey].oldValue)
            ) {
              // If the most recent edit is the same as the old value, its been reverted
              // and can be removed from edits
              isDeleteKey = true;
            } else {
              // the change is new, but preserve the original old values, so the new change
              // is always compared to the original old value
              currentChange.oldValue = prevState[changeKey].oldValue;
              currentChange.oldValueDict = prevState[changeKey].oldValueDict;
              currentChange.oldValueDisplay =
                prevState[changeKey].oldValueDisplay;
            }
          }

          if (isDeleteKey) {
            delete stagedEditsUpdate[changeKey];
          } else {
            stagedEditsUpdate[changeKey] = currentChange;
          }
        }
        //console.log(stagedEditsUpdate);
        return stagedEditsUpdate;
      });

      const undoSizeUpdate = gridRef.current.api.getCurrentUndoSize();
      const redoSizeUpdate = gridRef.current.api.getCurrentRedoSize();

      setUndoSize(undoSizeUpdate);
      setRedoSize(redoSizeUpdate);
    },
    [gridRef]
  );

  const onCellValueChange = React.useCallback(
    (event: CellValueChangedEvent) => {
      const colDef = event.colDef as any;
      const colMeta = colDef.meta as IRegistryDataColumn;

      let mapping_key = "";

      if (colMeta.mappings?.length) {
        mapping_key = colMeta.mappings[0].registry_key;
      }
      //@Katrisa
      let currentChange: IEditTransaction = {
        recordId: event.data[ColumnKeyEnum.record_id],
        fieldVersionId: colMeta.fieldversion_id,
        fieldVersionMapping: mapping_key,
        isPhi: colMeta.is_phi,
        isRequired: colMeta.is_required,
        field: event.colDef.field as string,
        oldValue: event.oldValue,
        oldValueDisplay: event.oldValue,
        newValue: event.newValue,
        newValueDisplay: event.newValue,
      };

      if (colMeta.type === RegistryDataColumnTypeEnum.file) {
        // Check if the old/new value has the dict coming in and pull file name out
        // Note: whent the undo is clicked, it sends string
        if (event.oldValue.hasOwnProperty("filename")) {
          currentChange.oldValue = event.oldValue.filename;
          currentChange.oldValueDisplay = event.oldValue.filename;
        }
        if (event.newValue.hasOwnProperty("filename")) {
          currentChange.newValue = event.newValue.filename;
          currentChange.newValueDisplay = event.newValue.filename;
        }
        if (event.newValue.hasOwnProperty("fileLocation")) {
          currentChange.s3Location = event.newValue.fileLocation;
        }
      }

      if (colMeta.type === RegistryDataColumnTypeEnum.dropdown) {
        // If dropdown, need to map the displayed value back to the backend value
        const options = colMeta.options;

        if (options) {
          Object.entries(options).forEach(([key, option]) => {
            if (option.label === event.oldValue) {
              currentChange.oldValue = option.value;
            } else if (option.label === event.newValue) {
              currentChange.newValue = option.value;
            }
          });
        }
      } else if (colMeta.type === RegistryDataColumnTypeEnum.radio) {
        // If dropdown, need to map the displayed value back to the backend value
        const options = colMeta.options;

        if (options) {
          Object.entries(options).forEach(([key, option]) => {
            if (option.label === event.oldValue) {
              currentChange.oldValue = option.value;
            } else if (option.label === event.newValue) {
              currentChange.newValue = option.value;
            }
          });
        }
      } else if (colMeta.type === RegistryDataColumnTypeEnum.yesno) {
        // If dropdown, need to map the displayed value back to the backend value
        const options = COL_OPTIONS_YESNO;

        Object.entries(options).forEach(([key, option]) => {
          if (option.label === event.oldValue) {
            currentChange.oldValue = option.value;
          } else if (option.label === event.newValue) {
            currentChange.newValue = option.value;
          }
        });
      }

      if (colMeta.type === RegistryDataColumnTypeEnum.checkbox) {
        // Checkbox data values have to be unwrapped
        const oldValuesLabels = event.oldValue;
        const newValuesLabels = event.newValue;

        const oldValuesUpdateDict = {};
        const newValueUpdateDict = {};

        const checkboxOptions = colMeta.options;

        if (checkboxOptions) {
          Object.entries(checkboxOptions).forEach(([key, option]) => {
            if (option && option.lookup_key) {
              oldValuesUpdateDict[option.lookup_key] = 0;
              newValueUpdateDict[option.lookup_key] = 0;

              if (newValuesLabels.includes(option.label)) {
                newValueUpdateDict[option.lookup_key] = 1;
              }
              if (oldValuesLabels.includes(option.label)) {
                oldValuesUpdateDict[option.lookup_key] = 1;
              }
            }
          });
        }

        currentChange = {
          ...currentChange,
          oldValueDict: oldValuesUpdateDict,
          newValueDict: newValueUpdateDict,
          isDict: true,
        };
      }
      //console.log(currentChange);

      handleEditEvent(currentChange, event);
    },
    [handleEditEvent]
  );

  const handleOnCloseEditsReviewModal = () => {
    setPreparedEdits([]);
    onCloseEditsReviewModal();
  };

  const handleOnSuccessEdits = () => {
    const gridApi = gridRef.current.api;

    gridApi.refreshCells();
    gridApi.stopEditing(true);
    onCloseEditsReviewModal();
    setIsEditMode({ isEditMode: false });
  };

  React.useEffect(() => {
    const gridApi = gridRef?.current?.api;
    if (gridApi) {
      gridApi.addEventListener("cellValueChanged", onCellValueChange);
    }

    return () => {
      if (gridApi) {
        gridApi.removeEventListener("cellValueChanged", onCellValueChange);
      }
    };
  }, [isGridReady, gridRef, onCellValueChange]);

  return (
    <>
      <Box>
        <HStack
          justifyContent={"center"}
          alignItems="center"
          py="2"
          px="6"
          bg="warning"
          spacing={0}
        >
          <ButtonGroup
            flex={1}
            variant={"solid"}
            size="sm"
            spacing={6}
            _dark={{
              color: "text.contrast",
            }}
            // colorScheme="whiteAlpha"
          >
            <Tooltip label="Undo Change">
              <Button
                onClick={handleUndo}
                leftIcon={<MaterialIcon name="undo" fontSize="1rem" />}
                aria-label="undo change"
                isDisabled={undoSize === 0}
              >
                Undo {undoSize ? `(${undoSize})` : ""}
              </Button>
            </Tooltip>
            <Tooltip label="Redo Change">
              <Button
                onClick={handleRedo}
                rightIcon={<MaterialIcon name="redo" fontSize="1rem" />}
                aria-label="redo change"
                isDisabled={redoSize === 0}
              >
                Redo {redoSize ? `(${redoSize})` : ""}
              </Button>
            </Tooltip>
          </ButtonGroup>
          <Box
            flex={2}
            justifyContent="center"
            _dark={{
              color: "text.contrast",
            }}
          >
            <Text fontSize={"lg"} fontWeight="bold" textAlign={"center"}>
              Edit Mode
            </Text>
          </Box>
          <ButtonGroup
            flex={1}
            justifyContent="flex-end"
            size="sm"
            spacing={6}
            _dark={{
              color: "text.contrast",
            }}
          >
            <Button
              variant="solid"
              colorScheme={"gray"}
              onClick={handleCloseEditMode}
              aria-label="cancel edit mode"
            >
              Cancel
            </Button>

            <Button
              colorScheme={"green"}
              onClick={handleSaveEdits}
              leftIcon={<MaterialIcon name="save" fontSize="1rem" />}
              aria-label="save changes"
              isDisabled={Object.keys(stagedEdits).length === 0}
            >
              Save changes ({Object.keys(stagedEdits).length})
            </Button>
          </ButtonGroup>
        </HStack>
      </Box>

      {registryId && isOpenEditsReviewModal && (
        <EditsReviewModal
          registryId={registryId}
          isOpen={isOpenEditsReviewModal}
          onClose={handleOnCloseEditsReviewModal}
          onSuccess={handleOnSuccessEdits}
          edits={preparedEdits}
        />
      )}
    </>
  );
};

export default RegistryEditMode;
